import React, { useCallback, useEffect, useState } from "react";
import moment from "moment";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import PageHeader from "../components/PageHeader";
import Help from "../components/Help";
import Paginate from "../components/pagination";
import DeleteModal from "../components/DeleteModal";
import CommonModal from "../components/CommonModal";
import { helpPagesData } from "../utils/helper";
import { requestWithToken } from "../utils/apis";
import { storyMapList } from "../action/storyMaps";
import trash from "../assets/trash.svg";
import { fetchSettings, UpdateLocationBlogsVisibility } from "../action/settingaction";
import SwitchToggle from "../components/switch";

const LocationBlogs = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);
  const [deleteLoader, setDeleteLoader] = useState(false);
  const [storyMapData, setStoryMapData] = useState({
    loading: false,
    data: [],
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
  });

  const [settings, setSettings] = useState(null);
  const [isListingEnabled, setIsListingEnabled] = useState(false);
  const [loadingSettings, setLoadingSettings] = useState(true);

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingValue, setPendingValue] = useState(null);
  const [toggleLoader, setToggleLoader] = useState(false);

  const loadSettings = async () => {
    try {
      const res = await fetchSettings();
      if (res?.status === 1) {
        setSettings(res.data);
        setIsListingEnabled(res.data?.location_blogs_enabled || false);
      }
    } catch (error) {
      toast.error("Failed to load settings");
    } finally {
      setLoadingSettings(false);
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  const handleToggleClick = (e) => {
    e.preventDefault();
    const newValue = !isListingEnabled;
    setPendingValue(newValue);
    setShowConfirmModal(true);
  };

  const confirmToggleListing = async () => {
    if (pendingValue === null) return;
    setToggleLoader(true);
    const newValue = pendingValue;

    try {
      const res = await UpdateLocationBlogsVisibility({ location_blogs_enabled: newValue });
      if (res?.status === 200 || res?.status === 1) {
        setIsListingEnabled(newValue);
        toast.success(res?.message || `Location Blog listing ${newValue ? "visibility enabled" : "visibility disabled"} successfully.`);
      } else {
        toast.error(res?.message || "Unable to update the listing visibility. Please try again.");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "An unexpected error occurred. Please try again.");
    } finally {
      setToggleLoader(false);
      setShowConfirmModal(false);
      setPendingValue(null);
    }
  };

  const fetchStoryMaps = useCallback(async () => {
    setStoryMapData((prev) => ({ ...prev, loading: true }));
    try {
      const result = await storyMapList(pagination.page, pagination.limit);
      if (result?.status) {
        setPagination((prev) => ({
          ...prev,
          total: Math.ceil((result.data?.total || 0) / prev.limit) || 0,
        }));
        setStoryMapData({
          data: result?.data?.list || [],
          loading: false,
        });
      } else {
        setStoryMapData({ data: [], loading: false });
      }
    } catch (error) {
      setStoryMapData({ data: [], loading: false });
      toast.error("Failed to fetch location blogs");
    }
  }, [pagination.page, pagination.limit]);

  useEffect(() => {
    fetchStoryMaps();
  }, [fetchStoryMaps]);

  const handleDelete = async (id) => {
    try {
      const result = await requestWithToken("DELETE", `/api/story-maps/${id}`);
      if (result?.status === 200) {
        setStoryMapData((prev) => ({
          ...prev,
          data: prev.data.filter((item) => item._id !== id),
        }));
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
        "Something went wrong. Please try again."
      );
    }
  };

  const confirmDelete = async () => {
    if (!fileToDelete) return;
    setDeleteLoader(true);
    await handleDelete(fileToDelete);
    setFileToDelete(null);
    setDeleteLoader(false);
    setShowDeleteModal(false);
  };

  const handlePageChange = (page) => {
    const newParams = new URLSearchParams(location.search);
    newParams.set("page", page);
    newParams.set("limit", pagination.limit);
    navigate(`?${newParams.toString()}`, { replace: true });
    setPagination((prev) => ({ ...prev, page }));
  };

  const handlePageSizeChange = (limit) => {
    const newParams = new URLSearchParams(location.search);
    newParams.set("page", 1);
    newParams.set("limit", limit);
    navigate(`?${newParams.toString()}`, { replace: true });
    setPagination((prev) => ({ ...prev, limit, page: 1 }));
  };

  const { loading, data = [] } = storyMapData;

  return (
    <>
      <PageHeader pageTitle="Location Blogs">
        <Help
          title={helpPagesData.pages.title}
          message={helpPagesData.pages.message}
          items={helpPagesData.pages.items}
        />
        <NavLink to="/location-blogs/add" className="btn-black">
          Create New
        </NavLink>
      </PageHeader>
      <div className="page-container">
        <div className="theme-card p-4">
          <div className="d-flex align-items-center justify-content-between pb-3">
            <div className="card-title">Overview</div>
            <div className="d-flex align-items-center">
              {!loadingSettings && (
                <>
                  <span className="me-2" style={{ fontSize: "14px", fontWeight: "500", color: "#6c757d" }}>
                    Enable Listing
                  </span>
                  <div onClick={handleToggleClick} style={{ cursor: "pointer", display: "inline-block" }}>
                    <SwitchToggle
                      checked={isListingEnabled}
                      onChange={() => {}}
                    />
                  </div>
                </>
              )}
            </div>
          </div>
          <div className="table-responsive">
            <table className="pages-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Slug</th>
                  <th>Status</th>
                  <th>Locations</th>
                  <th>Updated</th>
                  <th style={{ textAlign: "center" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="6" style={{ textAlign: "center" }}>
                      <Spinner size="md" />
                    </td>
                  </tr>
                ) : data.length === 0 ? (
                  <tr>
                    <td colSpan="6" style={{ textAlign: "center" }}>
                      No data found.
                    </td>
                  </tr>
                ) : (
                  data.map((item) => (
                    <tr
                      key={item._id}
                      onClick={() => navigate(`/location-blogs/${item._id}`)}
                      style={{ cursor: "pointer" }}
                    >
                      <td>{item.title || "-"}</td>
                      <td>{item.slug || "-"}</td>
                      <td>
                        {item?.is_published ? (
                          <span className="status success">Published</span>
                        ) : (
                          <span className="status danger">Draft</span>
                        )}
                      </td>
                      <td>{item.story_map?.length || 0}</td>
                      <td>
                        {item.updatedAt
                          ? moment(item.updatedAt).format("DD/MM/YYYY")
                          : "-"}
                      </td>
                      <td style={{ textAlign: "center" }}>
                        <button
                          className="delete-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            setFileToDelete(item._id);
                            setShowDeleteModal(true);
                          }}
                        >
                          <img src={trash} alt="Delete" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            <div className="px-3 pb-3">
              {Boolean(pagination?.total) && (
                <Paginate
                  currentPage={pagination.page}
                  totalPages={pagination.total}
                  onPageChange={handlePageChange}
                  pageSize={pagination.limit}
                  onPageSizeChange={handlePageSizeChange}
                />
              )}
            </div>
          </div>
        </div>
      </div>
      <DeleteModal
        show={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onDelete={confirmDelete}
        deleting={deleteLoader}
      />
      <CommonModal show={showConfirmModal} onClose={() => setShowConfirmModal(false)}>
        <div className="modal-card-body p-3">
          <div className="modal-card-content text-center">
            <div className="message-title mb-2 fs-5 fw-bold">Confirm Visibility Change</div>
            <div className="message-text mb-4 text-muted" style={{ fontSize: "14px" }}>
              Are you sure you want to {pendingValue ? "enable" : "disable"} the location blog listing visibility?
            </div>
          </div>
          <div className="card-footer-box d-flex justify-content-end gap-2">
            <button onClick={() => setShowConfirmModal(false)} className="btn-grey ms-auto me-2">
              Cancel
            </button>
            <button onClick={confirmToggleListing} className="btn-black" disabled={toggleLoader}>
              {toggleLoader ? <Spinner size="sm" /> : "Confirm"}
            </button>
          </div>
        </div>
      </CommonModal>
    </>
  );
};

export default LocationBlogs;
